import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FormControl, FormHelperText, Grid, Text, FormErrorMessage, NumberInput, NumberInputField } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../components/checkbox/checkbox';
import { DatePickerComponent } from '../../components/datepicker-component/datepicker-component';
import IconLabel from '../../components/icon-label/iconlabel';
import { Input } from '../../components/input/input';
import { Select } from '../../components/select/select';
import { InputSize } from '../../core/enums';
import { SubHeader } from '../organizations/subheader';
import { optionConverter } from './dictionary-utils';
import { styles } from './dictionary-styles';
import { cmxDateTime } from '@codametrix/ui-common';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
const hasError = (key, errors)=>{
    return errors && key in errors;
};
const getInlineError = (errors)=>{
    return errors.join(' ');
};
const displayErrorIfPresent = (key, errors)=>{
    return hasError(key, errors) ? getInlineError(errors[key]) : '';
};
const buildInlineMsg = (isError, field, errors)=>{
    return /*#__PURE__*/ _jsxs(FormControl, {
        isInvalid: isError,
        children: [
            field.helpText && /*#__PURE__*/ _jsx(FormHelperText, {
                children: /*#__PURE__*/ _jsx(IconLabel, {
                    leftIcon: /*#__PURE__*/ _jsx(InfoOutlineIcon, {}),
                    isLeft: true,
                    label: field.helpText
                })
            }),
            isError && /*#__PURE__*/ _jsx(FormErrorMessage, {
                children: /*#__PURE__*/ _jsx(IconLabel, {
                    isLeft: true,
                    leftIcon: /*#__PURE__*/ _jsx(InfoOutlineIcon, {}),
                    label: displayErrorIfPresent(field.key, errors)
                })
            })
        ]
    });
};
const Form = (props)=>{
    const { children, _className, dataItem = {}, errors, formDefinition, enable, onAction, onPropChange, onSave, showFieldSet = true, title } = props;
    const { fieldGroups = [], buttons = [], fields } = formDefinition;
    const classes = styles();
    const [data, setData] = useState(dataItem);
    const errs = errors.fieldErrors;
    useEffect(()=>{
        if (dataItem) setData(dataItem);
    }, [
        dataItem
    ]);
    const handleInputChange = (event)=>{
        const src = event.target;
        const { name = '', value = '' } = src;
        let val = value;
        if (src.getAttribute('type') === 'checkbox') {
            val = src.checked;
        }
        setData((prevState)=>({
                ...prevState,
                [name]: val
            }));
        onPropChange && onPropChange(name, value);
    };
    const handleDateChange = (date, name)=>{
        setData((prevState)=>({
                ...prevState,
                [name]: date
            }));
    };
    const handleNumberChange = (number, name)=>{
        setData((prevState)=>({
                ...prevState,
                [name]: number
            }));
        onPropChange && onPropChange(name, number);
    };
    const buildFormContent = (fieldGroup)=>fieldGroup?.fields?.map((field)=>{
            return buildField(field);
        });
    const buildField = (field)=>{
        const isError = hasError(field.key, errs);
        switch(field.type){
            case 'text':
                return /*#__PURE__*/ _jsx(Input, {
                    label: field.label,
                    placeholder: field.label,
                    size: InputSize.MD,
                    isRequired: field.required,
                    value: data[field.key],
                    isReadOnly: field.readonly,
                    name: field.key,
                    classes: {
                        root: classes.input,
                        input: classes.inputField
                    },
                    dataTestId: field.label,
                    textInfo: field.helpText,
                    onChange: handleInputChange,
                    isError: hasError(field.key, errs),
                    textError: displayErrorIfPresent(field.key, errs),
                    isDisabled: field.isDisabled
                });
            case 'select':
                return /*#__PURE__*/ _jsxs(Grid, {
                    sx: classes.input,
                    children: [
                        field.label && /*#__PURE__*/ _jsx(Text, {
                            sx: classes.label,
                            children: field.label
                        }),
                        /*#__PURE__*/ _jsx(Select, {
                            items: optionConverter(field),
                            name: field.name,
                            dataTestId: field.name,
                            classes: {
                                root: classes.input,
                                button: classes.selectField
                            },
                            value: data[field.key],
                            onChange: handleInputChange
                        }),
                        buildInlineMsg(isError, field, errs)
                    ]
                });
            case 'checkbox':
                return /*#__PURE__*/ _jsxs(Grid, {
                    sx: classes.input,
                    children: [
                        /*#__PURE__*/ _jsx(Checkbox, {
                            label: field.label ?? '',
                            name: field.key,
                            isChecked: data[field.key] !== false,
                            onChange: handleInputChange,
                            value: data[field.key],
                            dataTestId: field.label,
                            disabled: field.isDisabled
                        }),
                        buildInlineMsg(isError, field, errs)
                    ]
                });
            case 'date':
                return /*#__PURE__*/ _jsxs(Grid, {
                    sx: classes.input,
                    children: [
                        field.label && /*#__PURE__*/ _jsx(Text, {
                            sx: classes.label,
                            children: field.label
                        }),
                        /*#__PURE__*/ _jsx(DatePickerComponent, {
                            startDate: data[field.key] ? new Date(cmxDateTime.format(data[field.key], cmxDateTime.FORMATS.DATE)) : null,
                            endDate: null,
                            label: field.label,
                            showRange: false,
                            classNames: {
                                input: classes.datepickerInput,
                                rightIcon: classes.inputField
                            },
                            onSelection: (date)=>{
                                date && handleDateChange(date, field.key);
                            },
                            selectsRange: false,
                            iconRight: /*#__PURE__*/ _jsx(BiomeIcon, {
                                iconName: IconName.Calendar,
                                iconSize: "16",
                                iconClass: `${classes.calendarIcon}`,
                                titleText: "Calendar"
                            })
                        }),
                        buildInlineMsg(isError, field, errs)
                    ]
                });
            case 'number':
                const additionalProperties = {
                    ...field.additionalProperties
                };
                return /*#__PURE__*/ _jsxs(Grid, {
                    sx: classes.input,
                    children: [
                        field.label && /*#__PURE__*/ _jsx(Text, {
                            sx: classes.label,
                            children: field.label
                        }),
                        /*#__PURE__*/ _jsx(NumberInput, {
                            ...additionalProperties,
                            defaultValue: parseInt(field.placeholder ?? ''),
                            size: InputSize.MD,
                            isRequired: field.required,
                            value: data[field.key],
                            isReadOnly: field.readonly,
                            name: field.key,
                            onChange: (numAsString)=>handleNumberChange(numAsString, field.key),
                            isDisabled: field.isDisabled,
                            children: /*#__PURE__*/ _jsx(NumberInputField, {})
                        })
                    ]
                });
            default:
                console.warn('Field type not supported');
                break;
        }
    };
    const handleLinkButtonClick = ()=>{
        onAction && onAction(new CustomEvent('click', {
            detail: {
                eventType: 'cancel'
            }
        }));
    };
    const handleActionClick = ()=>{
        onSave && onSave(data);
    };
    const getActionBtnName = buttons.find((btn)=>btn.buttonType === 'submit')?.buttonText;
    const getLinkBtnName = buttons.find((btn)=>btn.buttonType === 'link')?.buttonText ?? '';
    return /*#__PURE__*/ _jsxs(Box, {
        children: [
            buttons && /*#__PURE__*/ _jsx(SubHeader, {
                actionName: getActionBtnName,
                onActionClick: handleActionClick,
                isActionDisabled: !enable(data),
                linkButtonName: getLinkBtnName?.charAt(0).toUpperCase() + getLinkBtnName?.slice(1),
                onLinkButtonClick: handleLinkButtonClick,
                title: title ?? '',
                className: _className
            }),
            /*#__PURE__*/ _jsxs(Grid, {
                sx: classes.fieldsets,
                children: [
                    fieldGroups?.map((fieldGroup)=>showFieldSet ? /*#__PURE__*/ _jsxs("fieldset", {
                            style: classes.fieldset,
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    style: classes.legend,
                                    children: fieldGroup.label
                                }),
                                /*#__PURE__*/ _jsx(Grid, {
                                    sx: classes.grid,
                                    children: buildFormContent(fieldGroup)
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(Grid, {
                            sx: classes.formContent,
                            children: buildFormContent(fieldGroup)
                        })),
                    fields?.map((field)=>{
                        return buildField(field);
                    }),
                    children
                ]
            })
        ]
    });
};
Form.displayName = 'Form';
export { Form };
